import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import { Switch, Checkbox } from 'pretty-checkbox-react';
import InputMask from 'react-input-mask';
import DatePicker, { registerLocale } from 'react-datepicker';
import objectId from 'bson-objectid';
/* actions */
import { getAllDepartments } from '../../redux/actions/departments';
import { getAllUsers } from '../../redux/actions/users';
import { getAllDevices } from '../../redux/actions/devices';
import {
  addReportSchemas,
  getAllReportSchemas,
  editReportSchemas,
  removeReportSchemas
} from '../../redux/actions/reportSchemas';
import {
  eventsReportContents,
  reportContentsDefault
} from '../../redux/actions/reportContents';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormHeader from '../../components/FormHeader';
import MultiSelect from '../../components/MultiSelect';
import Datatable from '../../components/Datatable/';
/* form-validation */
import schema from '../../form-validation/eventsReports';
import reportFields from './fields/events';
import eventsFields from './fields/typesEvents';
import ptBR from 'date-fns/locale/pt-BR';
registerLocale('ptBR', ptBR);

function Events () {
  const sortable = (a, b) => {
    return a.text.toLowerCase().localeCompare(b.text.toLowerCase());
  };

  const defaultButtons = [
    {
      extend: 'copy',
      className: 'btn btn-dark fa fa-copy',
      title: 'Relatorio Copiado',
      exportOptions: {
        columns: 'thead th:not(.no-export)'
      }
    },
    {
      extend: 'excel',
      className: 'btn btn-dark fa fa-file-excel',
      title: 'Relatorio Excel',
      exportOptions: {
        columns: 'thead th:not(.no-export)'
      }
    },
    {
      extend: 'csv',
      className: 'btn btn-dark fa fa-file-csv',
      title: 'Relatorio CSV',
      exportOptions: {
        columns: 'thead th:not(.no-export)'
      }
    },
    {
      extend: 'pdf',
      className: 'btn btn-dark fa fa-file-pdf',
      title: 'Relatorio PDF',
      exportOptions: {
        columns: 'thead th:not(.no-export)'
      }
    },
    {
      extend: 'print',
      className: 'btn btn-dark fa fa-file',
      title: 'Relatorio Print',
      exportOptions: {
        columns: 'thead th:not(.no-export)'
      }
    }
  ];

  const defaultColumns = reportFields.reduce((columns, field) => {
    const { id, text, title, render, checked } = field;
    const column = {
      title: text,
      tooltip: title,
      data: id,
      render: render || (data => data)
    };
    if (checked) {
      columns.push(column);
    }
    return columns;
  }, []);

  const hourRegex = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
  const dateRegex = /(^(((0[1-9]|1[0-9]|2[0-8])[/](0[1-9]|1[012]))|((29|30|31)[/](0[13578]|1[02]))|((29|30)[/](0[4,6,9]|11)))[/](19|[2-9][0-9])\d\d$)|(^29[/]02[/](19|[2-9][0-9])(00|04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96)$)/;
  const dateStart = new Date();
  dateStart.setHours(0, 0, 0, 0);
  const dateEnd = new Date();
  dateEnd.setHours(23, 59, 59, 0);

  const INITIAL_VALUES = {
    name: '',
    fields: reportFields.filter(field => field.checked).map(field => field.id),
    interval: [dateStart, dateEnd],
    events: ['calls'],
    departments: [],
    users: [],
    extensions: [],
    groups: [],
    lines: [],
    clients: [''],
    type: 'E'
  };

  const {
    register,
    handleSubmit,
    errors,
    setError,
    setValue,
    clearError
  } = useForm({
    validationSchema: schema
  });
  const authentications = useSelector(state => state.authentications);
  const reportSchemas = useSelector(state => state.reportSchemas);
  const reportContents = useSelector(state => state.reportContents);
  const devices = useSelector(state => state.devices.data);
  const departments = useSelector(state => state.departments.data);
  const users = useSelector(state => state.users.data);
  const dispatch = useDispatch();
  const [newReportSchema, setNewReportSchema] = useState(INITIAL_VALUES);
  const [data, setData] = useState(INITIAL_VALUES);
  const [idCurrentReportSchema, setIdCurrentReportSchema] = useState('');
  const [nameReportSchema, setNameReportSchema] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const submitButton = useRef();
  const nameReportSelect = useRef();
  const [hourStart, setHourStart] = useState(dateStart.toLocaleTimeString());
  const [hourEnd, setHourEnd] = useState(dateEnd.toLocaleTimeString());
  const [selectedOptions, setSelectedOptions] = useState({
    departments: [],
    users: [],
    extensions: [],
    groups: [],
    lines: []
  });
  const [buttons, setButtons] = useState([...defaultButtons]);
  const [columns, setColumns] = useState([...defaultColumns]);
  /* multiselect state */
  const [dpts, setDpts] = useState([]);
  const [usrs, setUsrs] = useState([]);
  const [exts, setExts] = useState([]);
  const [grps, setGrps] = useState([]);
  const [lns, setLns] = useState([]);
  const reportSchemasData = reportSchemas.data.filter(
    item => item.type === INITIAL_VALUES.type
  );

  useEffect(() => {
    dispatch(reportContentsDefault('events'));
    dispatch(getAllDepartments());
    dispatch(getAllUsers());
    dispatch(getAllDevices());
    const user = authentications.user._id;
    dispatch(getAllReportSchemas({ type: INITIAL_VALUES.type, user }));
  }, [dispatch, INITIAL_VALUES.type, authentications.user._id]);

  useEffect(() => {
    if (departments.length) {
      const dpts = departments
        .map(dpt => ({ value: dpt._id, text: dpt.name }))
        .sort(sortable);
      setDpts([...dpts]);
    }
  }, [departments, setDpts]);

  useEffect(() => {
    if (users.length) {
      const usrs = users
        .map(user => ({
          value: user._id,
          text: `${user.name} - Dpto: ${user.department.name}`,
          dpto: user.department._id
        }))
        .sort(sortable);
      setUsrs([...usrs]);
    }
  }, [users, setUsrs]);

  useEffect(() => {
    if (devices.length) {
      const exts = devices
        .filter(device => device.type === 'E')
        .map(ext => ({
          value: ext._id,
          text: `${ext.number} - Dpto: ${ext.department.name}`,
          dpto: ext.department._id
        }))
        .sort(sortable);
      setExts([...exts]);
    }
  }, [devices, setExts]);

  useEffect(() => {
    if (devices.length) {
      const grps = devices
        .filter(device => device.type === 'G')
        .map(grp => ({
          value: grp._id,
          text: `${grp.number} | ${grp.name} - Dpto: ${grp.department.name}`,
          dpto: grp.department._id
        }))
        .sort(sortable);
      setGrps([...grps]);
    }
  }, [devices, setGrps]);

  useEffect(() => {
    if (devices.length) {
      const lns = devices
        .filter(device => device.type === 'L')
        .map(line => ({
          value: line._id,
          text: `${line.number} - Dpto: ${line.department.name}`,
          dpto: line.department._id
        }))
        .sort(sortable);
      setLns([...lns]);
    }
  }, [devices, setLns]);

  const submit = () => {
    const [dateStart, dateEnd] = data.interval;
    const hourStart = document.getElementsByName('hourStart')[0].value;
    const hourEnd = document.getElementsByName('hourEnd')[0].value;
    const start = hourStart.split(':').map(n => parseInt(n, 10));
    const end = hourEnd.split(':').map(n => parseInt(n, 10));
    dateStart.setHours(start[0], start[1], start[2]);
    dateEnd.setHours(end[0], end[1], end[2]);
    setData({ ...data, interval: [dateStart, dateEnd] });
    const documentTitle = `Eventos_${dateStart.toLocaleDateString()}_${dateStart.toLocaleTimeString()}_${dateEnd.toLocaleDateString()}_${dateEnd.toLocaleTimeString()}`;
    setButtons(buttons.map(btn => ({ ...btn, title: documentTitle })));
    const columns = reportFields.reduce((fields, field) => {
      const { id, text, title, render } = field;
      const column = {
        title: text,
        tooltip: title,
        data: id,
        render: render || (data => (data || String(data) === '0' ? data : ''))
      };
      if (data.fields.includes(id)) {
        fields.push(column);
      }
      return fields;
    }, []);
    setColumns([...columns]);
    const clients = data.clients.filter(client => client);
    const dataRequest = {
      ...data,
      clients
    };
    dispatch(eventsReportContents(dataRequest, submitButton.current));
  };

  const handleChangeFields = evt => {
    const { name } = evt.target;
    if (!data.fields.includes(name)) {
      const { fields } = data;
      fields.push(name);
      setData({ ...data, fields });
    } else {
      const { fields } = data;
      const position = fields.findIndex(field => field === name);
      fields.splice(position, 1);
      setData({ ...data, fields });
    }
    const fields = [];
    for (const field of document.getElementsByClassName('report-fields')) {
      const { name, checked } = field.firstChild;
      fields.push({ name, checked });
    }
    const fieldAlreadyExists = fields.find(field => field.checked);
    const errorName = 'fields';
    if (fieldAlreadyExists) {
      setError(errorName);
    } else {
      setError(errorName, 'some', 'No items selected');
    }
  };

  const handleChangeDateInterval = (date, position) => {
    const { interval } = data;
    interval[position] = date;
    setData({ ...data, interval });
  };

  const handleChangeTimeInterval = evt => {
    evt.preventDefault();
    const { name, value } = evt.target;
    if (name === 'hourStart') {
      setHourStart(value);
      if (hourRegex.test(value)) {
        const [hour, min, sec] = value.split(':');
        const { interval } = data;
        if (interval[0]) {
          interval[0].setHours(hour, min, sec);
          setError(name);
          setValue({ [name]: value });
        }
      }
    } else if (name === 'hourEnd') {
      setHourEnd(value);
      if (hourRegex.test(value)) {
        const [hour, min, sec] = value.split(':');
        const { interval } = data;
        if (interval[1]) {
          interval[1].setHours(hour, min, sec);
          setError(name);
          setValue({ [name]: value });
        }
      }
    }
  };

  const handleBlurTimeInterval = evt => {
    const { name, value } = evt.target;
    hourRegex.test(value)
      ? setError(name)
      : setError(name, 'hour', 'Invalid hour');
    setValue({ [name]: value });
  };

  const handleBlurDateInterval = evt => {
    const { name, value } = evt.target;
    dateRegex.test(value)
      ? setError(name)
      : setError(name, 'date', 'Invalid date');
    setValue({ [name]: value });
  };

  const handleChangeEvents = evt => {
    const { name } = evt.target;
    let qtdItemsSelected = 0;
    if (data.events.includes(name)) {
      const events = data.events.filter(evt => evt !== name);
      qtdItemsSelected = events.length;
      setData({ ...data, events });
    } else {
      const { events } = data;
      events.push(name);
      qtdItemsSelected = events.length;
      setData({ ...data, events });
    }
    if (!qtdItemsSelected) {
      setError('events', 'some', 'No items selected');
      setValue({ events: data.events });
    } else {
      setError('events');
    }
  };

  const handleMultiDeselect = (deselecteds, nameField) => {
    const options = selectedOptions[nameField].slice();
    deselecteds.forEach(option => options.splice(options.indexOf(option), 1));
    const values = options.map(opt => opt.value);
    const newOptions = { ...selectedOptions, [nameField]: options };
    const newData = { ...data, [nameField]: values };
    if (nameField === 'departments') {
      // deselect users
      const userOptions = selectedOptions.users.filter(usr =>
        values.includes(usr.dpto)
      );
      newOptions.users = userOptions;
      newData.users = userOptions;
      // deselect extensions
      const extOptions = selectedOptions.extensions.filter(ext =>
        values.includes(ext.dpto)
      );
      newOptions.extensions = extOptions;
      newData.extensions = extOptions;
      // deselect groups
      const groupOptions = selectedOptions.groups.filter(grp =>
        values.includes(grp.dpto)
      );
      newOptions.groups = groupOptions;
      newData.groups = groupOptions;
      // deselect lines
      const lineOptions = selectedOptions.lines.filter(ln =>
        values.includes(ln.dpto)
      );
      newOptions.lines = lineOptions;
      newData.lines = lineOptions;
    }
    setSelectedOptions({ ...newOptions });
    setData({ ...newData });
  };

  const handleMultiSelect = (selecteds, nameField) => {
    const options = selecteds.slice();
    options.sort(sortable);
    setSelectedOptions({ ...selectedOptions, [nameField]: options });
    const values = options.map(opt => opt.value);
    setData({ ...data, [nameField]: values });
    setError(nameField);
    setValue({ [nameField]: values });
  };

  const handleChangeNameNewReportSchema = evt => {
    evt.preventDefault();
    const { name, value } = evt.target;
    setNewReportSchema({ ...newReportSchema, [name]: value });
  };

  const handleChangeNameUpdateReportSchema = evt => {
    evt.preventDefault();
    const { value } = evt.target;
    setNameReportSchema(value);
  };

  const handleClickSaveReportSchema = () => {
    const user = authentications.user._id;
    const _id = objectId().str;
    const { name } = newReportSchema;
    const resetForm = () => {
      setNewReportSchema({ ...INITIAL_VALUES });
      setIdCurrentReportSchema(_id);
    };
    dispatch(addReportSchemas({ ...data, _id, name, user }, resetForm));
  };

  const handleChangeSelectReportSchema = evt => {
    evt.preventDefault();
    const { value } = evt.target;
    const schema = reportSchemasData.find(
      schema => String(schema._id) === value
    );
    /* default options multiselect onchange */
    const options = {
      departments: [],
      users: [],
      extensions: [],
      groups: [],
      lines: []
    };
    if (schema) {
      const currentSchema = {
        ...INITIAL_VALUES,
        ...schema,
        interval: [new Date(schema.interval[0]), new Date(schema.interval[1])]
      };
      setHourStart(currentSchema.interval[0].toLocaleTimeString());
      setHourEnd(currentSchema.interval[1].toLocaleTimeString());
      setNameReportSchema(currentSchema.name);
      setIdCurrentReportSchema(currentSchema._id);
      /* modifica multiselect inclui os campos do modelo */
      options.departments = dpts.filter(dpto =>
        currentSchema.departments.includes(dpto.value)
      );
      options.users = usrs.filter(usr =>
        currentSchema.users.includes(usr.value)
      );
      options.extensions = exts.filter(ext =>
        currentSchema.extensions.includes(ext.value)
      );
      options.groups = grps.filter(grp =>
        currentSchema.groups.includes(grp.value)
      );
      options.lines = lns.filter(ln => currentSchema.lines.includes(ln.value));
      setSelectedOptions({ ...options });
      delete currentSchema._id;
      setData({ ...currentSchema });
    } else {
      setSelectedOptions({ ...options });
      setHourStart(dateStart.toLocaleTimeString());
      setHourEnd(dateEnd.toLocaleTimeString());
      setNameReportSchema('');
      setIdCurrentReportSchema('');
      setData({ ...INITIAL_VALUES });
    }
    clearError();
  };

  const handleClickCancelOperation = () => {
    setIsRemoving(false);
    setIsEditing(false);
  };

  const handleClickRemoveField = () => {
    setIsRemoving(true);
    setIsEditing(false);
  };

  const handleClickEditField = () => {
    setIsRemoving(false);
    setIsEditing(true);
    const schema = reportSchemasData.find(
      schema => String(schema._id) === String(idCurrentReportSchema)
    );
    setNameReportSchema(schema.name);
  };

  const handleClickRemoveSchema = () => {
    if (idCurrentReportSchema) {
      const callback = () => {
        setData({ ...INITIAL_VALUES });
        setIdCurrentReportSchema('');
        setIsRemoving(false);
      };
      dispatch(removeReportSchemas(idCurrentReportSchema, callback));
    }
  };

  const handleClickUpdateReportSchema = () => {
    if (idCurrentReportSchema) {
      let nameReport = '';
      let idCurrentReport = '';
      if (
        nameReportSelect &&
        nameReportSelect.current &&
        nameReportSelect.current.options
      ) {
        for (const opt of nameReportSelect.current.options) {
          if (opt.value === idCurrentReportSchema) {
            idCurrentReport = opt.value;
            nameReport = opt.innerHTML;
            break;
          }
        }
      }
      const newReportSchema = {
        ...data,
        _id: idCurrentReport || idCurrentReportSchema,
        name: nameReport || nameReportSchema
      };
      const callback = () => {
        setIsEditing(false);
      };
      dispatch(editReportSchemas(newReportSchema, callback));
    }
  };

  const handleChangeCheckedUncheckedAllFields = () => {
    if (data.fields.length === reportFields.length) {
      setData({ ...data, fields: [] });
    } else {
      const fields = reportFields.map(field => field.id);
      setData({ ...data, fields });
    }
  };

  const handleClickAddClient = () => {
    const { clients } = data;
    clients.push('');
    setData({ ...data, clients });
  };

  const handleClickRemoveClient = position => {
    const clients = [...data.clients];
    clients.splice(position, 1);
    setData({ ...data, clients });
  };

  const handleChangeClient = (evt, position) => {
    if (evt.target.value.match(/^\d+$/) || evt.target.value === '') {
      const { clients } = data;
      clients[position] = evt.target.value;
      setData({ ...data, clients });
    } else {
      evt.target.value = data.clients[position];
    }
  };

  return (
    <div className="container-fluid">
      {reportSchemas.addSuccess && (
        <AlertMsg
          type="success"
          msg="Modelo de relatório cadastrado com sucesso"
        />
      )}
      {reportSchemas.addError && (
        <AlertMsg
          type="danger"
          msg="Falha durante cadastro do modelo de relatório"
        />
      )}
      {reportSchemas.editSuccess && (
        <AlertMsg
          type="success"
          msg="Modelo de relatório atualizado com sucesso"
        />
      )}
      {reportSchemas.editError && (
        <AlertMsg
          type="danger"
          msg="Falha durante atualização do modelo de relatório"
        />
      )}
      {reportSchemas.removeSuccess && (
        <AlertMsg
          type="success"
          msg="Modelo de relatório removido com sucesso"
        />
      )}
      {reportSchemas.removeError && (
        <AlertMsg type="danger" msg="Falha ao remover modelo de relatório" />
      )}
      {!reportSchemas.addSuccess &&
        !reportSchemas.addError &&
        !reportSchemas.removeSuccess &&
        !reportSchemas.removeError &&
        !reportSchemas.editSuccess &&
        !reportSchemas.editError && (
        <AlertMsg type="info" msg="Área extração de relatório" />
      )}
      <div className="row">
        <div className="col-sm-3 col-md-6">
          <FormHeader title="Relatório de Eventos" />
        </div>
        <div className="col-sm-9 col-md-6">
          <div>
            <h5>Modelo de Relatórios</h5>
            <form>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  placeholder="Novo Modelo"
                  autoComplete="off"
                  value={newReportSchema.name}
                  onChange={handleChangeNameNewReportSchema}
                />
                <div className="input-group-append">
                  <button
                    className="btn btn-success"
                    type="button"
                    onClick={handleClickSaveReportSchema}
                    title="Adicionar modelo de relatório"
                    disabled={
                      !newReportSchema.name ||
                      reportSchemas.isFetching ||
                      isRemoving ||
                      isEditing
                    }
                  >
                    <i className="fa fa-plus" />
                  </button>
                </div>
              </div>
              <div className="input-group">
                <div className="mt-1">
                  <button
                    type="button"
                    className="btn btn-info rounded"
                    title="Salvar modificações"
                    onClick={handleClickUpdateReportSchema}
                    disabled={
                      !idCurrentReportSchema ||
                      reportSchemas.isFetching ||
                      isRemoving ||
                      isEditing
                    }
                  >
                    <i className="fa fa-save" />
                  </button>
                </div>
                {!isEditing ? (
                  <select
                    ref={nameReportSelect}
                    className="form-control mt-1 mr-1 ml-1 pl-2"
                    name="schema"
                    value={idCurrentReportSchema}
                    onChange={handleChangeSelectReportSchema}
                    disabled={!reportSchemasData.length || isRemoving}
                  >
                    <option value="">-- Selecione Modelo --</option>
                    {reportSchemasData.map((schema, i) => {
                      const _id = schema._id || Date.now() + '' + i;
                      return (
                        <option key={_id} value={_id}>
                          {schema.name}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control mt-1 mr-1 ml-1"
                    name="schema"
                    value={nameReportSchema}
                    onChange={handleChangeNameUpdateReportSchema}
                    autoFocus={true}
                  />
                )}
                <div className="mt-1">
                  <button
                    type="button"
                    className="btn btn-danger rounded"
                    onClick={handleClickCancelOperation}
                    disabled={
                      !(isEditing || isRemoving) || reportSchemas.isFetching
                    }
                    title="Cancelar operação"
                  >
                    <i className="fa fa-ban" />
                  </button>
                  <button
                    type="button"
                    className={`btn ${
                      isEditing ? 'btn-primary' : 'btn-warning'
                    } rounded ml-1 mr-1`}
                    onClick={() =>
                      !isEditing
                        ? handleClickEditField()
                        : handleClickUpdateReportSchema()
                    }
                    title={
                      !isEditing
                        ? 'Editar nome do relatório'
                        : 'Salvar modificação'
                    }
                    disabled={
                      (isEditing && !nameReportSchema) ||
                      !idCurrentReportSchema ||
                      reportSchemas.isFetching ||
                      isRemoving
                    }
                  >
                    <i className={`fa ${isEditing ? 'fa-save' : 'fa-pen'}`}></i>
                  </button>
                  <button
                    type="button"
                    disabled={
                      !idCurrentReportSchema ||
                      reportSchemas.isFetching ||
                      isEditing
                    }
                    onClick={() =>
                      !isRemoving
                        ? handleClickRemoveField()
                        : handleClickRemoveSchema()
                    }
                    className={`btn ${
                      isRemoving ? 'btn-info' : 'btn-danger'
                    } rounded`}
                    title={
                      !isRemoving
                        ? 'Excluir modelo de relatório'
                        : 'Confirmar exclusão'
                    }
                  >
                    <i
                      className={`fa ${
                        isRemoving ? 'fa-check-square' : 'fa-trash'
                      }`}
                    ></i>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit(submit)}>
        <div className="form-group mb-0 mt-2">
          <label className="mb-1">Campos&nbsp;</label>
          <Switch
            type="checkbox"
            color="success"
            onChange={handleChangeCheckedUncheckedAllFields}
            checked={data.fields.length === reportFields.length}
          >
            {data.fields.length === reportFields.length
              ? 'Desmarcar'
              : 'Marcar'}{' '}
            Todos
          </Switch>
          <div className="form-group border m-0">
            <div className="row mt-1">
              <div className="w-50 ml-4">
                {[...Array(Math.ceil(reportFields.length / 2))].map(
                  (_, position) => {
                    const { id, text, title } = reportFields[position];
                    return (
                      <div className="form-group mb-1" key={id}>
                        <div className="checkbox">
                          <span title={title}>
                            <Switch
                              className="report-fields"
                              type="checkbox"
                              color="primary"
                              name={id}
                              onChange={handleChangeFields}
                              checked={data.fields.includes(id)}
                            >
                              {text}
                            </Switch>
                          </span>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
              <div className="ml-4">
                {[
                  ...Array(
                    reportFields.length - Math.ceil(reportFields.length / 2)
                  )
                ].map((_, position) => {
                  const { id, text, title } = reportFields[
                    position + Math.ceil(reportFields.length / 2)
                  ];
                  return (
                    <div className="form-group mb-1" key={id}>
                      <div className="checkbox">
                        <span title={title}>
                          <Switch
                            className="report-fields"
                            type="checkbox"
                            color="primary"
                            name={id}
                            onChange={handleChangeFields}
                            checked={data.fields.includes(id)}
                          >
                            {text}
                          </Switch>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {errors.fields && errors.fields.type === 'some' ? (
            <ErrorMsgField type="some" msg="Nenhum campo selecionado" />
          ) : (
            <small className="form-text m-0">&nbsp;</small>
          )}
        </div>
        <div className="form-group mb-1">
          <label className="mb-1">Intervalo</label>
          <div className="form-group pt-1 pb-0 pl-2 pr-2 mb-0 border">
            <div className="form-row align-items-center">
              <div className="form-group col-sm-3 mb-0">
                <label>
                  <i className="fa fa-calendar-alt" /> Início
                </label>
                <DatePicker
                  className="form-control input-date"
                  name="dateStart"
                  onChange={date => handleChangeDateInterval(date, 0)}
                  onBlur={handleBlurDateInterval}
                  selected={data.interval[0]}
                  placeholderText="dd/MM/yyyy"
                  locale="ptBR"
                  dateFormat="dd/MM/yyyy"
                  autoComplete="off"
                  customInput={<input type="text" date={data.interval[0]} />}
                />
                {errors.dateStart && errors.dateStart.type === 'date' && (
                  <ErrorMsgField type="date" />
                )}

                {(!errors.dateStart ||
                  ((errors.dateStart && errors.dateStart.type === '') ||
                    errors.dateStart.type === 'start')) && (
                  <small className="form-text m-0">&nbsp;</small>
                )}
              </div>
              <div className="form-group col-sm-3 mb-0">
                <label>
                  <i className="far fa-clock" /> Início
                </label>
                <InputMask
                  name="hourStart"
                  mask="99:99:99"
                  value={hourStart}
                  className="form-control input-hour"
                  placeholder="HH:mm:ss"
                  onChange={handleChangeTimeInterval}
                  onBlur={handleBlurTimeInterval}
                  ref={register}
                />
                {errors.hourStart && errors.hourStart.type === 'hour' ? (
                  <ErrorMsgField type="hour" />
                ) : (
                  <small className="form-text m-0">&nbsp;</small>
                )}
              </div>
              <div className="form-group col-sm-3 mb-0">
                <label>
                  <i className="fa fa-calendar-alt" /> Final
                </label>
                <DatePicker
                  className="form-control input-date"
                  name="dateEnd"
                  onChange={date => handleChangeDateInterval(date, 1)}
                  selected={data.interval[1]}
                  placeholderText="dd/MM/yyyy"
                  locale="ptBR"
                  dateFormat="dd/MM/yyyy"
                  autoComplete="off"
                  customInput={<input type="text" date={data.interval[1]} />}
                />
                {errors.dateEnd && errors.dateEnd.type === 'date' && (
                  <ErrorMsgField type="date" />
                )}
                {(!errors.dateEnd ||
                  (errors.dateEnd && errors.dateEnd.type === '')) && (
                  <small className="form-text m-0">&nbsp;</small>
                )}
              </div>
              <div className="form-group col-sm-3 mb-0">
                <label>
                  <i className="far fa-clock" /> Final
                </label>
                <InputMask
                  name="hourEnd"
                  mask="99:99:99"
                  value={hourEnd}
                  className="form-control input-hour"
                  placeholder="HH:mm:ss"
                  onChange={handleChangeTimeInterval}
                  onBlur={handleBlurTimeInterval}
                  ref={register}
                />
                {errors.hourEnd && errors.hourEnd.type === 'hour' ? (
                  <ErrorMsgField type="hour" />
                ) : (
                  <small className="form-text m-0">&nbsp;</small>
                )}
              </div>
            </div>
          </div>
          {errors.dateStart && errors.dateStart.type === 'start' ? (
            <ErrorMsgField
              type="start"
              msg="Intervalo de inicio deve ser menor que o de final"
            />
          ) : (
            <small className="form-text m-0">&nbsp;</small>
          )}
        </div>
        <div className="form-group mb-1">
          <label className="mb-1">Eventos</label>
          <div className="form-group pt-1 pb-0 pl-2 pr-2 mb-0 border">
            <div className="form-row align-items-center">
              {eventsFields.map((field, position) => {
                return (
                  <div className="form-group mb-0 col" key={position}>
                    <div className="form-group mb-1">
                      <div className="checkbox">
                        <Checkbox
                          className="event-fields"
                          type="checkbox"
                          color="primary-o"
                          name={field.id}
                          animation="smooth"
                          onChange={handleChangeEvents}
                          checked={data.events.includes(field.id)}
                          inputProps={{ name: field.id }}
                        >
                          {field.text}
                        </Checkbox>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {errors.events && errors.events.type === 'some' ? (
            <ErrorMsgField type="some" msg="Nenhum campo selecionado" />
          ) : (
            <small className="form-text m-0">&nbsp;</small>
          )}
        </div>
        <div className="form-group mb-1">
          <label className="mb-1">Departamentos</label>
          <div className="form-group pt-1 pb-0 pl-2 pr-2 mb-0 border">
            <MultiSelect
              avaliableOptions={dpts}
              selectedOptions={selectedOptions.departments}
              handleMultiDeselect={options =>
                handleMultiDeselect(options, 'departments')
              }
              handleMultiSelect={options =>
                handleMultiSelect(options, 'departments')
              }
              valueProp="value"
              textProp="text"
              title=""
              name="departments"
              ref={register}
              error={!!errors.departments && !!errors.departments.type}
            />
          </div>
          {errors.departments && errors.departments.type === 'required' ? (
            <ErrorMsgField
              type="required"
              msg="Nenhum departamento selecionado"
            />
          ) : (
            <small className="form-text m-0">&nbsp;</small>
          )}
        </div>
        <div className="form-group mb-1">
          <label className="mb-1">Usuários</label>
          <div className="form-group pt-1 pb-0 pl-2 pr-2 mb-0 border">
            <MultiSelect
              avaliableOptions={usrs.filter(usr =>
                data.departments.includes(usr.dpto)
              )}
              selectedOptions={selectedOptions.users}
              handleMultiDeselect={options =>
                handleMultiDeselect(options, 'users')
              }
              handleMultiSelect={options => handleMultiSelect(options, 'users')}
              valueProp="value"
              textProp="text"
              title=""
              name="users"
              ref={register}
              error={!!errors.users && !!errors.users.type}
            />
          </div>
          {errors.users && errors.users.type === 'required' ? (
            <ErrorMsgField type="required" msg="Nenhum item selecionado" />
          ) : (
            <small className="form-text m-0">&nbsp;</small>
          )}
        </div>
        <div className="form-group mb-1">
          <label className="mb-1">Ramais</label>
          <div className="form-group pt-1 pb-0 pl-2 pr-2 mb-0 border">
            <MultiSelect
              avaliableOptions={exts.filter(ext =>
                data.departments.includes(ext.dpto)
              )}
              selectedOptions={selectedOptions.extensions}
              handleMultiDeselect={options =>
                handleMultiDeselect(options, 'extensions')
              }
              handleMultiSelect={options =>
                handleMultiSelect(options, 'extensions')
              }
              valueProp="value"
              textProp="text"
              title=""
              name="extensions"
              ref={register}
              error={!!errors.extensions && !!errors.extensions.type}
            />
          </div>
          {errors.extensions && errors.extensions.type === 'required' ? (
            <ErrorMsgField type="required" msg="Nenhum item selecionado" />
          ) : (
            <small className="form-text m-0">&nbsp;</small>
          )}
        </div>
        <div className="form-group mb-1">
          <label className="mb-1">Grupos</label>
          <div className="form-group pt-1 pb-0 pl-2 pr-2 mb-0 border">
            <MultiSelect
              avaliableOptions={grps.filter(grp =>
                data.departments.includes(grp.dpto)
              )}
              selectedOptions={selectedOptions.groups}
              handleMultiDeselect={options =>
                handleMultiDeselect(options, 'groups')
              }
              handleMultiSelect={options =>
                handleMultiSelect(options, 'groups')
              }
              valueProp="value"
              textProp="text"
              title=""
              name="groups"
              ref={register}
              error={!!errors.groups && !!errors.groups.type}
            />
          </div>
          {errors.groups && errors.groups.type === 'required' ? (
            <ErrorMsgField type="required" msg="Nenhum item selecionado" />
          ) : (
            <small className="form-text m-0">&nbsp;</small>
          )}
        </div>
        <div className="form-group mb-1">
          <label className="mb-1">Linhas</label>
          <div className="form-group pt-1 pb-0 pl-2 pr-2 mb-0 border">
            <MultiSelect
              avaliableOptions={lns.filter(ln =>
                data.departments.includes(ln.dpto)
              )}
              selectedOptions={selectedOptions.lines}
              handleMultiDeselect={options =>
                handleMultiDeselect(options, 'lines')
              }
              handleMultiSelect={options => handleMultiSelect(options, 'lines')}
              valueProp="value"
              textProp="text"
              title=""
              name="lines"
              ref={register}
              error={!!errors.lines && !!errors.lines.type}
            />
          </div>
          {errors.lines && errors.lines.type === 'required' ? (
            <ErrorMsgField type="required" msg="Nenhum item selecionado" />
          ) : (
            <small className="form-text m-0">&nbsp;</small>
          )}
        </div>
        <div className="mb-4 p-2 border">
          <div className="form-group">
            <button
              type="button"
              className="btn btn-dark"
              onClick={handleClickAddClient}
            >
              <i className="fa fa-plus"></i>&nbsp; Adicionar Número
            </button>
          </div>
          {data.clients.map((value, position) => {
            return (
              <div
                key={position}
                className="form-group p-2 m-0"
                style={{
                  backgroundColor: position % 2 === 0 ? '#ddd' : '#fff'
                }}
              >
                <div className="form-row">
                  <div className="form-group col-sm-10 m-0">
                    <input
                      type="text"
                      className="form-control"
                      value={value}
                      onChange={evt => handleChangeClient(evt, position)}
                      placeholder={`${position + 1}${String.fromCharCode(
                        176
                      )} Número do Telefône`}
                    />
                  </div>
                  <div className="col-sm-2">
                    <button
                      type="button"
                      className="btn btn-danger btn-block rounded-0"
                      disabled={!position}
                      onClick={() => handleClickRemoveClient(position)}
                    >
                      <i className="fa fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="row">
          <div className="container-fluid mb-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() =>
                document
                  .getElementsByClassName('page-content')[0]
                  .scrollTo({ top: 0, left: 0, behavior: 'smooth' })
              }
            >
              <i className="fa fa-arrow-up"></i>
              &nbsp;Início Formulário
            </button>
            <button
              type="submit"
              className="btn btn-primary float-right"
              ref={submitButton}
            >
              <i className="fa fa-table"></i>
              &nbsp;Gerar Relatório
            </button>
          </div>
        </div>
      </form>
      <div className="mt-3 container-reports">
        {reportContents.isFetching ? (
          <div className="fa-2x text-center">
            <span>
              Carregando <i className="fas fa-spinner fa-spin"></i>
            </span>
          </div>
        ) : (
          <div>
            <Datatable
              data={reportContents.data.events}
              columns={columns}
              buttons={buttons}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Events;
